import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import * as Sentry from "@sentry/react";
import { PostHogProvider } from 'posthog-js/react';
import App from './App.tsx';
import './index.css';

// PostHog analytics configuration
const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-11-30',
} as const;

Sentry.init({
  dsn: "https://289ce17d48e514ab3b0b68a2c2700a9b@o4509483231215616.ingest.de.sentry.io/4509483241373776",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true
});

// Create a client with settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (keep data in cache)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Create a localStorage persister for offline-first experience
// Messages and conversations load instantly from cache while background refresh happens
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'fmf-query-cache', // Key prefix for localStorage
  throttleTime: 1000, // Only save to storage at most once per second
});

const container = document.getElementById("root");
const root = ReactDOM.createRoot(container!);
root.render(
  <React.StrictMode>
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={posthogOptions}
    >
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: 1000 * 60 * 60 * 24, // 24 hours - keep cache for a day
            dehydrateOptions: {
              // Only persist specific queries (messages, conversations, entity IDs)
              shouldDehydrateQuery: (query) => {
                const key = query.queryKey[0];
                // Only persist messaging-related queries and entity IDs
                return key === 'messages' || key === 'conversations' || key === 'entity-id';
              },
            },
          }}
        >
          <App />
        </PersistQueryClientProvider>
      </BrowserRouter>
    </PostHogProvider>
  </React.StrictMode>
);
